import type { baloot, GameView, hearts, king, pisti, spades, tarneeb } from "@app/rules";
import { FACTS } from "./facts";

/** The trick-taking games: a trick in the middle, a last trick, tricks won per seat. */
export type TrickView =
  | GameView
  | spades.SpadesView
  | tarneeb.TarneebView
  | baloot.BalootView
  | hearts.HeartsView
  | king.KingView;

/** Games played onto a pile in the middle (Pişti): no trick anywhere in the view (ADR 0050). */
export type PileView = pisti.PistiView;

/**
 * Every engine view the client can render; `variant` picks the presentation. Shared code reads
 * only what all of them carry; whoever needs the trick asks for a `TrickView`.
 */
export type AnyView = TrickView | PileView;

export function isPile(view: AnyView): view is PileView {
  return view.variant === "pisti";
}

export function isTrickGame(view: AnyView): view is TrickView {
  return !isPile(view);
}

/** Views with a bid range, a pass and a chosen trump (Batak, Tarneeb): the same sheets serve both. */
export type BiddingView = GameView | tarneeb.TarneebView;

export function isSpades(view: AnyView): view is spades.SpadesView {
  return view.variant === "spades";
}

/** Partnership games carry team scores next to the seat scores. */
export type TeamView =
  | spades.SpadesView
  | tarneeb.TarneebView
  | baloot.BalootView
  | pisti.PistiView;

export function isTeamGame(view: AnyView): view is TeamView {
  return FACTS[view.variant].teams;
}

export function isKing(view: AnyView): view is king.KingView {
  return view.variant === "king";
}

export function isHearts(view: AnyView): view is hearts.HeartsView {
  return view.variant === "hearts";
}

/** Hearts: this seat still owes its three cards, along with whoever else does (ADR 0046). */
export function isChoosingPass(view: AnyView, seat: number | null): boolean {
  return isHearts(view) && seat !== null && view.pass.pending.includes(seat);
}

export function isBaloot(view: AnyView): view is baloot.BalootView {
  return view.variant === "baloot";
}
